/** Pure Audit display mapping — no I/O. D4I-A. Presentation only. */

import type { StatusTone } from "./overview-ops";

export type AuditListRow = {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  summary: string;
  createdAt: string;
  actor: { id: string; displayName: string; email: string } | null;
};

export type AuditSortKey = "time" | "action" | "actor" | "entity";

export type StatusDisplay = { label: string; tone: StatusTone; detail?: string };

export const AUDIT_PAGE_NOTE =
  "سابقه تغییرات رویدادهای مهم CMS را به‌صورت فقط‌خواندنی و append-only ثبت می‌کند. دامنهٔ فهرست همیشه سازمان فعلی است.";

export const AUDIT_PRIVACY_NOTE =
  "این صفحه IP، User-Agent، کوکی، توکن، رمز، دادهٔ نشست، جزئیات وب‌هوک، یا dump خام درخواست را نمایش نمی‌دهد.";

const SECRETISH =
  /password|passwd|secret|token|cookie|authorization|session|webhook|api[_-]?key|private[_-]?key|bearer|csrf|mfa|raw.?payload|user.?agent|\bip\b/i;

/** Common action → Dari label (fallback: technical action string). */
const ACTION_LABELS: Record<string, string> = {
  "content.published": "نشر محتوا",
  "content.scheduled": "زمان‌بندی محتوا",
  "content.archived": "بایگانی محتوا",
  "content.created": "ایجاد محتوا",
  "content.updated": "به‌روزرسانی محتوا",
  "content.paused": "توقف محتوا",
  "content.unpaused": "از سرگیری محتوا",
  "content.rollback": "بازگردانی محتوا",
  "content.approval.submitted": "ارسال برای تأیید",
  "content.approval.rejected": "رد تأیید",
  "site_group.created": "ایجاد گروه سایت",
  "site_group.updated": "به‌روزرسانی گروه سایت",
  "site_group.overlaps_resolved": "رفع هم‌پوشانی گروه",
  "site.created": "ایجاد سایت",
  "site.updated": "به‌روزرسانی سایت",
  "sponsored.created": "ایجاد کمپین",
  "sponsored.updated": "به‌روزرسانی کمپین",
  "sponsored.submitted": "ارسال کمپین",
  "sponsored.rejected": "رد کمپین",
  "sponsored.paused": "توقف کمپین",
  "sponsored.unpaused": "از سرگیری کمپین",
  "sponsored.archived": "بایگانی کمپین",
  "sponsored.rolled_back": "بازگردانی کمپین",
  "reseller.profile.created": "ایجاد پروفایل نماینده",
  "reseller.profile.updated": "به‌روزرسانی پروفایل نماینده",
  "reseller.profile.published": "انتشار پروفایل نماینده",
  "reseller.profile.archived": "بایگانی پروفایل نماینده",
  "portal.bundle.job.requested": "درخواست Bundle",
  "portal.bundle.job.completed": "آماده‌سازی Bundle",
  "portal.bundle.job.failed": "شکست Bundle",
  "user.created": "ایجاد کاربر",
  "user.updated": "به‌روزرسانی کاربر",
  "user.bootstrap": "راه‌اندازی اولیه",
  "org.switched": "تعویض سازمان",
  "org.membership.invited": "دعوت عضو",
  "org.membership.activated": "فعال‌سازی عضو",
  "org.membership.role_changed": "تغییر نقش عضو",
  "org.membership.suspended": "تعلیق عضو",
  "org.membership.removed": "حذف عضو",
  "auth.login.success": "ورود موفق",
  "auth.login.failure": "ورود ناموفق",
  "auth.logout": "خروج",
  "auth.mfa.enrolled": "ثبت MFA",
  "auth.mfa.verified": "تأیید MFA",
  "auth.mfa.reset": "بازنشانی MFA",
  "auth.organization.switched": "تعویض سازمان",
  "settings.updated": "به‌روزرسانی تنظیمات",
  "remote_content.kill_switch.enabled": "فعال‌سازی Kill Switch",
  "remote_content.kill_switch.disabled": "غیرفعال‌سازی Kill Switch",
};

export function auditActionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export function auditActionTone(action: string): StatusTone {
  if (/publish|create|verify|match/i.test(action)) return "success";
  if (/delete|revoke|cancel|kill|suspend|archive/i.test(action)) return "warning";
  if (/fail|mismatch|reject/i.test(action)) return "danger";
  return "info";
}

export function auditOutcomeDisplay(action: string): StatusDisplay {
  return {
    label: auditActionLabel(action),
    tone: auditActionTone(action),
    detail: "رکورد ممیزی ثبت‌شده — نه اجرای مجدد عمل",
  };
}

export function safeTargetLabel(entityType: string, entityId: string | null): string {
  if (!entityType && !entityId) return "—";
  if (entityId) return `${entityType}:${entityId}`;
  return entityType;
}

/** Strip accidental secret-looking fragments from summary text for display. */
export function sanitizeAuditSummary(summary: string): string {
  if (!summary) return "—";
  if (SECRETISH.test(summary)) {
    return "خلاصهٔ ایمن‌سازی‌شده (جزئیات حساس حذف شد)";
  }
  return summary;
}

/** Subject line for compact feeds — drops English audit prefix when present. */
export function auditSummarySubject(summary: string): string {
  const safe = sanitizeAuditSummary(summary);
  if (!safe || safe === "—") return "—";
  const colonIdx = safe.indexOf(": ");
  if (colonIdx >= 0) {
    const subject = safe.slice(colonIdx + 2).trim();
    if (subject) return subject;
  }
  return safe;
}

export function matchesAuditQuery(row: AuditListRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    row.action,
    auditActionLabel(row.action),
    row.entityType,
    row.entityId ?? "",
    row.summary,
    row.actor?.displayName ?? "",
    row.actor?.id ?? "",
    row.id,
  ]
    .join(" ")
    .toLowerCase()
    .includes(q);
}

export function matchesAuditEntityFilter(row: AuditListRow, filter: string): boolean {
  if (filter === "all") return true;
  return row.entityType === filter;
}

export function filterAndSortAudit(
  rows: AuditListRow[],
  opts: { query: string; entityFilter: string; sort: AuditSortKey },
): AuditListRow[] {
  const filtered = rows.filter(
    (r) => matchesAuditQuery(r, opts.query) && matchesAuditEntityFilter(r, opts.entityFilter),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "action") return a.action.localeCompare(b.action);
    if (opts.sort === "actor") {
      return (a.actor?.displayName ?? "").localeCompare(b.actor?.displayName ?? "", "fa");
    }
    if (opts.sort === "entity") {
      return safeTargetLabel(a.entityType, a.entityId).localeCompare(
        safeTargetLabel(b.entityType, b.entityId),
        "en",
      );
    }
    return b.createdAt.localeCompare(a.createdAt);
  });
  return sorted;
}

export function uniqueEntityTypes(rows: AuditListRow[]): string[] {
  return [...new Set(rows.map((r) => r.entityType).filter(Boolean))].sort();
}

export type RelatedAuditLink = { id: string; label: string; to: string };

export function relatedAuditLinks(): RelatedAuditLink[] {
  return [
    { id: "overview", label: "نمای کلی", to: "/" },
    { id: "observability", label: "پایش عملیات", to: "/observability" },
    { id: "versions", label: "تاریخچه انتشار", to: "/versions" },
  ];
}
